import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriptionPlan, TenantSubscription, DashboardOverviewResponse, AddOnPurchase, CostBreakdownItem } from '../types';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import ShinyText from '../components/ui/ShinyText';
import DuoToggle from '../components/ui/DuoToggle';
import { Plus, BookOpen, Workflow } from 'lucide-react';
import { api } from '../services/api';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { handleApiError } from '../utils/errorHandling';
import { getPlanTier } from '../utils/planTierConfig';
import { useLocale } from '../context/LocaleContext';

interface GetPlansResponse {
    plans: SubscriptionPlan[];
}

const SubscriptionsPage: React.FC = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [currentSub, setCurrentSub] = useState<TenantSubscription | null>(null);
    const [dashboardData, setDashboardData] = useState<DashboardOverviewResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submittingPlanId, setSubmittingPlanId] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'info'; message: string } | null>(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const { setSubscriptionError } = useSubscriptionError();

    // Cost breakdown state
    const [costBreakdownItems, setCostBreakdownItems] = useState<CostBreakdownItem[]>([]);

    // Add-on state
    const [purchasedAddOns, setPurchasedAddOns] = useState<AddOnPurchase[]>([]);
    const [expandedAddOn, setExpandedAddOn] = useState<string | null>(null);
    const [purchasingAddOn, setPurchasingAddOn] = useState<string | null>(null);
    const [showAddOnConfirm, setShowAddOnConfirm] = useState<{
        type: string;
        label: string;
        price: number;
        variant?: 'self_prepared' | 'mentoros_prepared';
    } | null>(null);
    const [addOnTab, setAddOnTab] = useState<'one_time' | 'recurring'>('one_time');
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState<{
        purchaseId: string;
        label: string;
    } | null>(null);

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                setNotification(null);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [notification]);

    const handleCancel = async () => {
        setShowCancelConfirm(true);
    };

    const confirmCancel = async () => {
        setError(null);
        setSubmittingPlanId('cancel');

        try {
            await api.delete<TenantSubscription>('/api/subscriptions/tenant');
            setCurrentSub(null);
            setNotification({ type: 'info', message: 'Your subscription has been cancelled successfully.' });
            setSubscriptionError(null);
        } catch (err: any) {
            console.error('Cancellation error:', err);
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setSubmittingPlanId(null);
            setShowCancelConfirm(false);
        }
    };

    const fetchSubscriptionData = async () => {
        try {
            const subResponse = await api.get<TenantSubscription>('/api/subscriptions/tenant').catch(e => {
                if (e.code === 'TENANT0004') {
                    return null;
                }

                if (e.message && e.message.includes('404')) {
                    return null;
                }

                throw e;
            });

            if (subResponse && (!subResponse.planId || !subResponse.status)) {
                console.warn('Invalid subscription data structure:', subResponse);
                return null;
            }

            setCurrentSub(subResponse);
            return subResponse;
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
            return null;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const dashboardPromise = api.getDashboardOverview()
                    .then(data => setDashboardData(data))
                    .catch(() => setDashboardData(null));

                const plansPromise = api.get<GetPlansResponse>('/api/subscriptions/plans');

                const [plansResponse] = await Promise.all([
                    plansPromise,
                    fetchSubscriptionData(),
                    dashboardPromise
                ]);

                api.getAddOnPurchases()
                    .then(res => setPurchasedAddOns(res.purchases || []))
                    .catch(() => setPurchasedAddOns([]));

                api.getCostBreakdown()
                    .then(res => setCostBreakdownItems(res.items || []))
                    .catch(() => setCostBreakdownItems([]));

                const validPlans =
                    plansResponse.plans?.filter(
                        plan =>
                            plan &&
                            plan.planId &&
                            plan.name &&
                            plan.pricing &&
                            typeof plan.pricing.amount === 'number'
                    ) || [];

                setPlans(validPlans);
                setSubscriptionError(null);
            } catch (err: any) {
                handleApiError(err, setError, setSubscriptionError);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handlePurchaseAddOn = async (
        type: string,
        quantity?: number,
        variant?: 'self_prepared' | 'mentoros_prepared'
    ) => {
        setPurchasingAddOn(type);

        try {
            const res = await api.purchaseAddOn({ type: type as any, quantity, variant });

            if (res.success && res.purchase) {
                setPurchasedAddOns(prev => [...prev, res.purchase]);
                setNotification({
                    type: 'success',
                    message: `Add-on "${res.purchase.label}" purchased successfully!`
                });
                setExpandedAddOn(null);
            }
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setPurchasingAddOn(null);
            setShowAddOnConfirm(null);
        }
    };

    const handleCancelAddOn = async (purchaseId: string, label: string) => {
        setPurchasingAddOn(purchaseId);
        setShowDeactivateConfirm(null);

        try {
            const res = await api.cancelAddonPurchase(purchaseId);

            if (res.purchase) {
                setPurchasedAddOns(prev =>
                    prev.map(addon =>
                        addon.id === purchaseId
                            ? {
                                    ...addon,
                                    status: res.purchase.status,
                                    cancelEffectiveAt: res.purchase.cancelEffectiveAt
                                }
                            : addon
                    )
                );
                setNotification({
                    type: 'info',
                    message: `Add-on "${label}" will be canceled at the end of this billing period.`
                });
            }
        } catch (err: any) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setPurchasingAddOn(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-full">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error) {
        return <div className="text-center text-red-500">{error}</div>;
    }

    const tier = currentSub?.planId ? getPlanTier(currentSub.planId) : 'Unknown';
    const tierDisplay = tier.charAt(0).toUpperCase() + tier.slice(1);

    const { formatCurrency, formatDate } = useLocale();

    const baseCost = currentSub?.billing?.amount || 0;
    const llmUsageCost = dashboardData?.data?.usageOverview?.monthlyCostToDate || 0;
    const addOnsTotalCost = purchasedAddOns.reduce((sum, addon) => sum + addon.totalPrice, 0);
    const breakdownItemsTotal = costBreakdownItems.reduce((sum, item) => sum + item.amount, 0);

    const defaultBreakdownItems: CostBreakdownItem[] = [
        {
            key: 'base_cost',
            label: 'Base Cost',
            sublabel: `${tierDisplay === 'Unknown' ? 'Standard' : tierDisplay} Plan`,
            amount: baseCost,
            currency: 'EUR'
        },
        {
            key: 'llm_usage',
            label: 'LLM Usage',
            sublabel: 'Live Cumulative',
            amount: llmUsageCost,
            currency: 'EUR'
        },
        {
            key: 'custom_solutions',
            label: 'Custom Solutions',
            sublabel: 'Add-Ons',
            amount: 0,
            currency: 'EUR'
        }
    ];

    const breakdownItems = costBreakdownItems.length > 0 ? costBreakdownItems : defaultBreakdownItems;
    const totalCost =
        costBreakdownItems.length > 0
            ? breakdownItemsTotal + addOnsTotalCost
            : baseCost + llmUsageCost + addOnsTotalCost;

    const isActiveRecurringStatus = (status?: AddOnPurchase['status']) =>
        status === 'active' || status === 'canceling';

    const isRecurringAddOnActive = (type: string) =>
        purchasedAddOns.some(
            addon =>
                addon.type === type &&
                addon.chargeModel === 'recurring' &&
                isActiveRecurringStatus(addon.status)
        );

    const domainKnowledgePurchase = purchasedAddOns.find(
        addon =>
            addon.type === 'domain_knowledge' &&
            addon.chargeModel === 'recurring' &&
            isActiveRecurringStatus(addon.status)
    );
    const isDomainKnowledgeActive = isRecurringAddOnActive('domain_knowledge');
    const isDomainKnowledgeCanceling = domainKnowledgePurchase?.status === 'canceling';

    const recurringCustomAddOns = purchasedAddOns.filter(
        addon => addon.type === 'custom_actions' && addon.chargeModel === 'recurring'
    );

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 dark:text-text-primary text-light-text-primary ">My Subscription</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Current Subscription Status</h2>
                
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            currentSub?.status === 'active' ? 'bg-green-500/20 text-green-500' :
                            currentSub?.status === 'trial' ? 'bg-blue-500/20 text-blue-500' :
                            'bg-gray-500/20 text-gray-500'
                        }`}>
                            {(currentSub?.status || 'No Active Subscription').toUpperCase()}
                        </span>
                    </div>

                    {currentSub?.planId && (
                         <div className="flex justify-between items-center">
                            <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Plan Type</span>
                            <span className="font-medium text-white dark:text-white text-gray-900">{tierDisplay}</span>
                         </div>
                    )}
                     
                     <div className="flex justify-between items-center">
                        <span className="text-text-secondary dark:text-text-secondary text-light-text-secondary">Next Billing</span>
                        <span className="text-white dark:text-white text-gray-900">
                             {currentSub?.billing?.nextBillingDate 
                                ? formatDate(currentSub.billing.nextBillingDate) 
                                : 'N/A'}
                        </span>
                     </div>

                     {currentSub?.status === 'active' && currentSub.planId && (
                        <div className="pt-4 mt-4 border-t border-border/50">
                             <Button 
                                variant="danger" 
                                className="w-full" 
                                onClick={handleCancel} 
                                isLoading={submittingPlanId === 'cancel'} 
                                disabled={submittingPlanId !== null}
                             >
                                Cancel Subscription
                             </Button>
                        </div>
                     )}
                </div>
            </Card>

            <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-text-secondary dark:text-text-secondary text-light-text-secondary">Cost Breakdown</h2>
                
                <div className="space-y-4">
                    {breakdownItems.map((item) => (
                        <div key={item.key} className="flex justify-between items-center pb-4 border-b border-border/50">
                            <div>
                                <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">{item.label}</div>
                                <div className="text-xs text-text-tertiary">{item.sublabel}</div>
                            </div>
                            <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(item.amount, item.currency || 'EUR')}</span>
                        </div>
                    ))}

                    {purchasedAddOns.map((addon) => (
                        <div key={addon.id} className="flex justify-between items-center pb-4 border-b border-border/50">
                            <div>
                                <div className="text-text-secondary dark:text-text-secondary text-light-text-secondary">{addon.label}</div>
                                <div className="text-xs text-text-tertiary">
                                    {addon.type === 'extra_conversations' ? `${addon.quantity}x conversations` : 'Add-on'}
                                </div>
                            </div>
                            <span className="font-mono text-lg dark:text-white text-gray-900">{formatCurrency(addon.totalPrice, 'EUR')}</span>
                        </div>
                    ))}

                    <div className="flex justify-between items-center pt-2">
                        <span className="font-semibold text-lg text-white dark:text-white text-gray-900">Total</span>
                        <span className="font-mono text-2xl font-bold text-primary">{formatCurrency(totalCost, 'EUR')}</span>
                    </div>
                </div>
            </Card>
        </div>

        <div className="text-center text-sm text-text-tertiary mt-8">
            * Costs are estimated and updated in real-time based on usage.
        </div>

        {/* Purchased Add-ons Section */}
        {purchasedAddOns.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border/30">
                <h2 className="text-2xl font-bold text-center mb-8 dark:text-text-primary text-light-text-primary">Purchased Add-ons</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {purchasedAddOns.map((addon) => (
                        <div
                            key={addon.id}
                            className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-4"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="font-semibold dark:text-white text-gray-900">{addon.label}</span>
                                    {addon.quantity && addon.quantity > 1 && (
                                        <span className="text-sm text-text-tertiary ml-2">x{addon.quantity}</span>
                                    )}
                                </div>
                                <span className="font-mono text-sm font-semibold text-primary">
                                    {formatCurrency(addon.totalPrice, addon.currency || 'EUR')}
                                </span>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-2">
                                    {addon.chargeModel === 'recurring' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Recurring</span>
                                    )}
                                    {addon.status === 'canceling' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500">
                                            Cancels {addon.cancelEffectiveAt ? formatDate(addon.cancelEffectiveAt) : 'at period end'}
                                        </span>
                                    )}
                                    {addon.status === 'active' && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">Active</span>
                                    )}
                                </div>
                                {addon.chargeModel === 'recurring' && addon.status === 'active' && (
                                    <Button
                                        size="sm"
                                        variant="secondary"
                                        className="text-xs"
                                        onClick={() => handleCancelAddOn(addon.id, addon.label)}
                                        isLoading={purchasingAddOn === addon.id}
                                        disabled={purchasingAddOn !== null}
                                    >
                                        Cancel
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* Available Add-ons Section */}
        <div className="mt-12 pt-8 border-t border-border/30">
            <h2 className="text-2xl font-bold text-center mb-6 dark:text-text-primary text-light-text-primary">Available Add-ons</h2>
            
            <div className="flex justify-center mb-8">
                <DuoToggle
                    options={[
                        { label: 'One-time Payment', value: 'one_time' },
                        { label: 'Recurring', value: 'recurring' }
                    ]}
                    value={addOnTab}
                    onChange={(val) => { setAddOnTab(val as 'one_time' | 'recurring'); setExpandedAddOn(null); }}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {addOnTab === 'one_time' ? (
                <>
                {/* Order Status Integration */}
                <div 
                    className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6 cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => setShowAddOnConfirm({ type: 'order_status', label: 'Order Status Integration', price: 80 })}
                >
                    <div className="flex items-start gap-3">
                        <div className="mt-1 text-primary">
                            <Plus className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <ShinyText text="Order Status Integration" className="font-semibold text-lg" />
                                <span className="font-mono text-sm font-semibold text-primary ml-2">{formatCurrency(80, 'EUR')}</span>
                            </div>
                            <p className="text-sm text-text-tertiary mt-1">
                                Connect Mentoros to your shipping/carrier or OMS API to deliver real-time order updates inside the conversation.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Custom Actions & Workflows (One-time) */}
                <div 
                    className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6 cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => navigate('/support')}
                >
                    <div className="flex items-start gap-3">
                        <div className="mt-1 text-primary">
                            <Plus className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <ShinyText text="Custom Actions & Workflows" className="font-semibold text-lg" />
                            <p className="text-sm text-text-tertiary mt-1">
                                Request custom in-store actions beyond the standard capabilities (e.g., specialized product configurators, custom quoting, or platform-specific flows).
                            </p>
                        </div>
                    </div>
                </div>
                </>
              ) : (
                <>
                {/* Domain Knowledge Pack (Monthly) */}
                <div className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6">
                    <div className="flex items-start gap-3">
                        <div className="mt-1 text-primary">
                            <BookOpen className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <ShinyText text="Domain Knowledge Pack" className="font-semibold text-lg" />
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs font-medium ${
                                        isDomainKnowledgeCanceling
                                          ? 'text-yellow-500'
                                          : isDomainKnowledgeActive
                                            ? 'text-green-500'
                                            : 'text-gray-400'
                                      }`}>
                                        {isDomainKnowledgeCanceling
                                          ? 'Canceling'
                                          : isDomainKnowledgeActive
                                            ? 'Active'
                                            : 'Inactive'}
                                    </span>
                                    <button
                                        onClick={() => {
                                            if (isDomainKnowledgeCanceling) {
                                                return;
                                            }

                                            if (domainKnowledgePurchase) {
                                                setShowDeactivateConfirm({
                                                    purchaseId: domainKnowledgePurchase.id,
                                                    label: 'Domain Knowledge Pack'
                                                });
                                            } else {
                                                setExpandedAddOn(expandedAddOn === 'domain_knowledge' ? null : 'domain_knowledge');
                                            }
                                        }}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                            isDomainKnowledgeCanceling
                                              ? 'bg-yellow-500'
                                              : isDomainKnowledgeActive
                                                ? 'bg-green-500'
                                                : 'bg-gray-600'
                                          }`}
                                    >
                                        <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${isDomainKnowledgeActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-text-tertiary mt-1">
                                Add industry-specific knowledge to match your niche (e.g., photography gear, skincare ingredients, automotive parts) using your approved resources. Billed monthly.
                            </p>
                        </div>
                    </div>
                    {!isDomainKnowledgeActive && expandedAddOn === 'domain_knowledge' && (
                        <div className="mt-4 pt-4 border-t border-border/50 space-y-3">
                            <Button
                                className="w-full justify-between"
                                variant="secondary"
                                onClick={() => setShowAddOnConfirm({ type: 'domain_knowledge', label: 'Domain Knowledge Pack (Monthly)', price: 40, variant: 'self_prepared' })}
                                disabled={purchasingAddOn !== null}
                            >
                                <span>I have knowledge ready</span>
                                <span className="font-mono font-semibold text-primary">{formatCurrency(40, 'EUR')}/mo</span>
                            </Button>
                            <Button
                                className="w-full justify-between"
                                variant="secondary"
                                onClick={() => setShowAddOnConfirm({ type: 'domain_knowledge', label: 'Domain Knowledge Pack (Monthly)', price: 120, variant: 'mentoros_prepared' })}
                                disabled={purchasingAddOn !== null}
                            >
                                <span>Prepare knowledge for me</span>
                                <span className="font-mono font-semibold text-primary">{formatCurrency(120, 'EUR')}/mo</span>
                            </Button>
                        </div>
                    )}
                </div>

                {/* Tenant-specific Recurring Custom Add-ons (dynamic from BE) */}
                {recurringCustomAddOns.map((addon) => {
                    const isActive = isActiveRecurringStatus(addon.status);
                    const isCanceling = addon.status === 'canceling';
                    return (
                        <div key={addon.id} className="dark:bg-surface bg-light-surface rounded-lg border dark:border-border-color border-light-border-color shadow-lg p-6">
                            <div className="flex items-start gap-3">
                                <div className="mt-1 text-primary">
                                    <Workflow className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <ShinyText text={addon.label} className="font-semibold text-lg" />
                                        <div className="flex items-center gap-2">
                                            <span className={`text-xs font-medium ${
                                                isCanceling
                                                  ? 'text-yellow-500'
                                                  : isActive
                                                    ? 'text-green-500'
                                                    : 'text-gray-400'
                                              }`}>
                                                {isCanceling ? 'Canceling' : isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <button
                                                onClick={() => {
                                                    if (isCanceling) {
                                                        return;
                                                    }

                                                    if (isActive) {
                                                        setShowDeactivateConfirm({
                                                            purchaseId: addon.id,
                                                            label: addon.label
                                                        });
                                                    } else {
                                                        navigate('/support');
                                                    }
                                                }}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                    isCanceling
                                                      ? 'bg-yellow-500'
                                                      : isActive
                                                        ? 'bg-green-500'
                                                        : 'bg-gray-600'
                                                  }`}
                                            >
                                                <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-sm text-text-tertiary mt-1">
                                        {addon.totalPrice > 0 ? `${formatCurrency(addon.totalPrice, addon.currency || 'EUR')}/${addon.billingPeriod === 'yearly' ? 'yr' : 'mo'}` : 'Custom pricing — contact support for details.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                })}
                </>
              )}
            </div>

            <p className="text-center text-sm text-text-tertiary mt-6">
                Add-ons can be applied to any plan. Contact sales for pricing and implementation details.
            </p>
        </div>

        {/* Add-on confirmation modal */}
        {showAddOnConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
                <Card title="Confirm Purchase" className="w-full max-w-md m-4">
                    <p className="dark:text-text-secondary text-light-text-secondary">
                        Purchase <span className="font-semibold dark:text-white text-gray-900">{showAddOnConfirm.label}</span> for <span className="font-semibold text-primary">{formatCurrency(showAddOnConfirm.price, 'EUR')}</span>?
                    </p>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                        This will be added to your current billing period.
                    </p>
                    <div className="flex justify-end gap-4 mt-6">
                        <Button variant="secondary" onClick={() => setShowAddOnConfirm(null)} disabled={purchasingAddOn !== null}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handlePurchaseAddOn(showAddOnConfirm.type, undefined, showAddOnConfirm.variant)}
                            isLoading={purchasingAddOn === showAddOnConfirm.type}
                        >
                            Confirm Purchase
                        </Button>
                    </div>
                </Card>
            </div>
        )}

        {/* Deactivation confirmation modal */}
        {showDeactivateConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
                <Card title="Deactivate Add-on" className="w-full max-w-md m-4">
                    <p className="dark:text-text-secondary text-light-text-secondary">
                        Are you sure you want to deactivate <span className="font-semibold dark:text-white text-gray-900">{showDeactivateConfirm.label}</span>?
                    </p>
                    <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                        The add-on will remain active until the end of your current billing period.
                    </p>
                    <div className="flex justify-end gap-4 mt-6">
                        <Button variant="secondary" onClick={() => setShowDeactivateConfirm(null)} disabled={purchasingAddOn !== null}>
                            Go Back
                        </Button>
                        <Button
                            onClick={() => handleCancelAddOn(showDeactivateConfirm.purchaseId, showDeactivateConfirm.label)}
                            isLoading={purchasingAddOn === showDeactivateConfirm.purchaseId}
                            disabled={purchasingAddOn !== null}
                        >
                            Confirm Deactivation
                        </Button>
                    </div>
                </Card>
            </div>
        )}

        {/* Mobile toast notification */}
        {notification && (
            <div className="fixed top-10 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)]">
            <Toast
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification(null)}
                duration={5000}
            />
            </div>
        )}

        {showCancelConfirm && currentSub && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80]">
            <Card title="Cancel Subscription" className="w-full max-w-md m-4">
                <p className="dark:text-text-secondary text-light-text-secondary">
                Are you sure you want to cancel your subscription?
                </p>
                <p className="text-sm dark:text-text-secondary text-light-text-secondary mt-2">
                Your access will continue until the end of your current billing period on {currentSub.billing?.nextBillingDate ? formatDate(currentSub.billing.nextBillingDate) : 'N/A'}.
                </p>
                <div className="flex justify-end gap-4 mt-6">
                <Button variant="secondary" onClick={() => setShowCancelConfirm(false)} disabled={submittingPlanId === 'cancel'}>
                    Go Back
                </Button>
                <Button
                    variant="danger"
                    onClick={confirmCancel}
                    isLoading={submittingPlanId === 'cancel'}
                >
                    Confirm Cancellation
                </Button>
                </div>
            </Card>
            </div>
        )}
    </div>
  );
};

export default SubscriptionsPage;
